// Additional Classical Two-Planet Combinations
// Based on BPHS and Jataka Parijata

import type { DashaContext, TransitContext, LifeArea, HouseNum, PlanetName } from './types';
import { weightByLevel, natureWeight, normHouseScore, transitReinforcement, aggregateScore } from './scoring';

export type Rule = {
  key: string;
  priority?: number;
  areas: LifeArea[];
  templateKey: string;
  customPrediction?: string;
  remedies?: string;
  ruleType?: 'timing' | 'natal';
  private?: boolean;
  triggers: (dasha: DashaContext, transits: TransitContext) => boolean;
  score: (dasha: DashaContext, transits: TransitContext) => { value:number; notes:string[]; customWindow?: { from: string; to: string } };
};

// ===== SUN-MOON COMBINATIONS =====

const sunMoonConjunction: Rule = {
  key: 'sun_moon_conjunction',
  areas: ['Self', 'Health', 'Career'],
  templateKey: 'sun_moon_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const sunHouse = dasha.natalPlacements.Sun?.house;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    
    // Debug logging
    if (sunHouse && moonHouse) {
      console.log('[Sun-Moon Conjunction] Sun house:', sunHouse, 'Moon house:', moonHouse, 'Match:', sunHouse === moonHouse);
    }
    
    return sunHouse !== undefined && sunHouse === moonHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`☀️🌙 SUN-MOON CONJUNCTION (New Moon Birth):`);
    notes.push(`CLASSICAL: Born on or near New Moon (Amavasya) - soul and mind united`);
    notes.push(`EFFECTS: Strong willpower, focused mind, what you want you pursue intensely`);
    notes.push(`CHARACTER: One-pointed determination, mind and ego aligned, stubborn persistence`);
    notes.push(`HEALTH: Sensitive to monthly lunar cycles, emotions affect vitality`);
    notes.push(`PSYCHOLOGY: Less internal conflict - mind and heart want same things`);
    notes.push(`CHALLENGE: Can be too self-focused, difficulty seeing others' perspectives`);
    notes.push(`PARASHARA: If in strong house (1,4,7,10,5,9), gives leadership and authority`);
    notes.push(`BEST IN: 1st (strong personality), 10th (career success), 9th (dharmic power)`);
    notes.push(`ACTIVATION: Sun dasha (6y) or Moon dasha (10y) brings unified purpose`);
    
    return { value: 35, notes };
  }
};

const sunMoonOpposition: Rule = {
  key: 'sun_moon_opposition',
  areas: ['Self', 'Spouse', 'Health'],
  templateKey: 'sun_moon_opposition',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const sunHouse = dasha.natalPlacements.Sun?.house;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!sunHouse || !moonHouse) return false;
    
    // Opposition: 7 houses apart
    const diff = Math.abs(sunHouse - moonHouse);
    return diff === 6 || diff === 7;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`☀️↔️🌙 SUN-MOON OPPOSITION (Full Moon Birth):`);
    notes.push(`CLASSICAL: Born on or near Full Moon (Purnima) - soul and mind in tension`);
    notes.push(`EFFECTS: Internal conflicts, what you want vs what you feel often differ`);
    notes.push(`PSYCHOLOGY: Mind-ego polarity, objective vs subjective viewpoints, self-awareness`);
    notes.push(`RELATIONSHIPS: Attracts strong partnerships, projects inner conflict onto relationships`);
    notes.push(`STRENGTH: Ability to see both sides, balanced perspective, diplomatic nature`);
    notes.push(`CHALLENGE: Indecision, inner contradictions, need external validation`);
    notes.push(`PARASHARA: Gives public recognition, fame, but emotional fluctuations`);
    notes.push(`BEST FOR: Public roles, counseling, mediation - seeing multiple perspectives`);
    notes.push(`REMEDIES: Balance Sun and Moon - father/mother relationship healing important`);
    
    return { value: 25, notes };
  }
};

// ===== SUN-JUPITER COMBINATIONS =====

const sunJupiterConjunction: Rule = {
  key: 'sun_jupiter_conjunction',
  areas: ['Father', 'Spirituality', 'Career', 'Wealth'],
  templateKey: 'sun_jupiter_conjunction',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const sunHouse = dasha.natalPlacements.Sun?.house;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    return sunHouse !== undefined && sunHouse === jupiterHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`☀️🪐 SUN-JUPITER CONJUNCTION (Guru-Aditya Yoga):`);
    notes.push(`CLASSICAL: Authority meets wisdom - one of most auspicious combinations!`);
    notes.push(`EFFECTS: Righteous authority, wise leadership, dharmic power, respect from all`);
    notes.push(`FATHER: Excellent father figure, learned and spiritual, guru-like qualities`);
    notes.push(`CHARACTER: Natural teacher, philosophical, ethical conduct, generous spirit`);
    notes.push(`CAREER: Government positions, teaching, religious leadership, advisory roles`);
    notes.push(`WEALTH: Prosperity through righteous means, generous but accumulates steadily`);
    notes.push(`PARASHARA: "Like a king among counselors" - authority enhanced by wisdom`);
    notes.push(`BEST IN: 1st, 5th, 9th, 10th houses - creates powerful raj yogas`);
    notes.push(`ACTIVATION: Sun dasha (6y) or Jupiter dasha (16y) brings recognition and wealth`);
    notes.push(`CAVEAT: If too close (<10°), Jupiter combustion reduces some benefits initially`);
    
    return { value: 60, notes };
  }
};

// ===== SUN-VENUS COMBINATIONS =====

const sunVenusConjunction: Rule = {
  key: 'sun_venus_conjunction',
  areas: ['Spouse', 'Wealth', 'Career'],
  templateKey: 'sun_venus_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const sunHouse = dasha.natalPlacements.Sun?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return sunHouse !== undefined && sunHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`☀️💎 SUN-VENUS CONJUNCTION (Malavya Bhanga):`);
    notes.push(`CLASSICAL: Authority meets beauty/luxury - complex combination`);
    notes.push(`EFFECTS: Artistic authority, creative leadership, charm in public roles`);
    notes.push(`RELATIONSHIPS: For males - spouse from good family, beautiful, but ego clashes possible`);
    notes.push(`RELATIONSHIPS: For females - strong personality, independent, may delay marriage`);
    notes.push(`CAREER: Success in arts, entertainment, luxury goods, diplomacy, beauty industry`);
    notes.push(`WEALTH: Money through creative pursuits, artistic ventures, luxury businesses`);
    notes.push(`COMBUSTION ISSUE: Venus often combust (within 10°) - delays in love/marriage`);
    notes.push(`PARASHARA: "Beauty without pride or pride without beauty" - balance needed`);
    notes.push(`REMEDY: Strengthen Venus through Friday rituals, white clothing, respect women`);
    notes.push(`ACTIVATION: Sun dasha delays relationships, Venus dasha brings romance and wealth`);
    
    return { value: 30, notes };
  }
};

// ===== MOON-JUPITER COMBINATIONS =====

const moonJupiterConjunction: Rule = {
  key: 'moon_jupiter_conjunction',
  areas: ['Self', 'Children', 'Spirituality', 'Wealth'],
  templateKey: 'moon_jupiter_conjunction',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    return moonHouse !== undefined && moonHouse === jupiterHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`🌙🪐 MOON-JUPITER CONJUNCTION (Chandra-Guru Yoga):`);
    notes.push(`CLASSICAL: Mind meets wisdom - creates wise, wealthy, generous person`);
    notes.push(`EFFECTS: Philosophical mind, optimistic nature, wealth, good children, happiness`);
    notes.push(`CHARACTER: Naturally generous, kind-hearted, religious inclination, loved by all`);
    notes.push(`MOTHER: Mother is wise, spiritual, educated - good maternal influence`);
    notes.push(`CHILDREN: Blessed with intelligent, well-behaved children, good fortune through them`);
    notes.push(`WEALTH: Financial prosperity, especially if in 2nd, 4th, 5th, 9th, or 11th house`);
    notes.push(`MIND: Optimistic thinking, philosophical approach to life, emotional stability`);
    notes.push(`PARASHARA: "The native becomes a minister or advisor to kings"`);
    notes.push(`BEST IN: Kendra (1,4,7,10) or Trikona (1,5,9) houses for maximum effect`);
    notes.push(`ACTIVATION: Moon dasha (10y) or Jupiter dasha (16y) brings peak prosperity`);
    
    return { value: 62, notes };
  }
};

// ===== MARS-JUPITER COMBINATIONS =====

const marsJupiterConjunction: Rule = {
  key: 'mars_jupiter_conjunction',
  areas: ['Career', 'Wealth', 'Property'],
  templateKey: 'mars_jupiter_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const marsHouse = dasha.natalPlacements.Mars?.house;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    return marsHouse !== undefined && marsHouse === jupiterHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`⚔️🪐 MARS-JUPITER CONJUNCTION (Guru-Mangal Yoga):`);
    notes.push(`CLASSICAL: Warrior meets priest - dynamic energy channeled righteously`);
    notes.push(`EFFECTS: Courageous yet ethical, strong principles, protective nature, leadership`);
    notes.push(`CAREER: Military officer, police inspector, sports coach, ethical business leader`);
    notes.push(`PROPERTY: Gains through real estate, land, construction - righteous means`);
    notes.push(`CHARACTER: Fights for justice, protects weak, stands up for dharma, noble warrior`);
    notes.push(`CHALLENGE: Impulsive religious enthusiasm, over-confidence in beliefs`);
    notes.push(`PARASHARA: "Skilled in weapons and scriptures both" - combines action and wisdom`);
    notes.push(`BEST IN: 1st, 3rd, 6th, 10th, 11th houses - gives victory over enemies`);
    notes.push(`REMEDIES: Hanuman worship (combines Mars-Jupiter), Tuesday-Thursday fasts`);
    notes.push(`ACTIVATION: Mars dasha (7y) or Jupiter dasha (16y) brings righteous success`);
    
    return { value: 52, notes };
  }
};

// ===== MARS-VENUS COMBINATIONS =====

const marsVenusConjunction: Rule = {
  key: 'mars_venus_conjunction',
  areas: ['Spouse', 'Wealth', 'Self'],
  templateKey: 'mars_venus_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const marsHouse = dasha.natalPlacements.Mars?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return marsHouse !== undefined && marsHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`⚔️💎 MARS-VENUS CONJUNCTION (Passion Meets Beauty):`);
    notes.push(`CLASSICAL: Warrior meets lover - intense desires and relationships`);
    notes.push(`EFFECTS: Magnetic attraction, passionate nature, artistic talent with drive`);
    notes.push(`RELATIONSHIPS: Intense romantic life, passionate partner, early attractions`);
    notes.push(`CHALLENGES: Relationship conflicts, desire-based decisions, multiple attractions`);
    notes.push(`POSITIVE: Creative energy, artistic pursuits, fashion, design, performing arts`);
    notes.push(`WEALTH: Money through beauty/arts + action - salons, gyms, sports fashion`);
    notes.push(`CHARACTER: Charming but aggressive, attractive but confrontational`);
    notes.push(`PARASHARA: "Strong desires need disciplined expression through creative channels"`);
    notes.push(`REMEDY: Channel energy into creative arts, sports, avoid impulsive relationships`);
    notes.push(`ACTIVATION: Mars dasha brings passionate romances, Venus dasha brings luxury`);
    
    return { value: 35, notes };
  }
};

// ===== MERCURY-JUPITER COMBINATIONS =====

const mercuryJupiterConjunction: Rule = {
  key: 'mercury_jupiter_conjunction',
  areas: ['Self', 'Career', 'Children'],
  templateKey: 'mercury_jupiter_conjunction',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    return mercuryHouse !== undefined && mercuryHouse === jupiterHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`📝🪐 MERCURY-JUPITER CONJUNCTION (Budha-Guru Yoga):`);
    notes.push(`CLASSICAL: Intelligence meets wisdom - creates brilliant scholars and teachers`);
    notes.push(`EFFECTS: Sharp intellect, philosophical mind, excellent communication, teaching ability`);
    notes.push(`EDUCATION: Multiple degrees, lifelong learner, expertise in many subjects`);
    notes.push(`CAREER: Professor, researcher, author, consultant, financial advisor, astrologer`);
    notes.push(`COMMUNICATION: Speaks with authority and wisdom, convincing arguments, ethical speech`);
    notes.push(`CHILDREN: Highly intelligent children, good at guiding and educating them`);
    notes.push(`PARASHARA: "Master of scriptures and sciences both" - encyclopedic knowledge`);
    notes.push(`BEST IN: 1st, 2nd, 5th, 9th, 10th houses for maximum intellectual brilliance`);
    notes.push(`CHALLENGE: Can be overly analytical or preachy - balance head and heart`);
    notes.push(`ACTIVATION: Mercury dasha (17y) or Jupiter dasha (16y) brings academic success`);
    
    return { value: 58, notes };
  }
};

// ===== MERCURY-VENUS COMBINATIONS =====

const mercuryVenusConjunction: Rule = {
  key: 'mercury_venus_conjunction',
  areas: ['Self', 'Career', 'Wealth'],
  templateKey: 'mercury_venus_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return mercuryHouse !== undefined && mercuryHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`📝💎 MERCURY-VENUS CONJUNCTION (Artistic Intelligence):`);
    notes.push(`CLASSICAL: Communication meets beauty - creates artists and entertainers`);
    notes.push(`EFFECTS: Artistic talent, beautiful speech, creative writing, musical ability`);
    notes.push(`CAREER: Writer, poet, singer, musician, actor, designer, advertising creative`);
    notes.push(`COMMUNICATION: Charming speaker, witty conversationalist, diplomatic language`);
    notes.push(`BUSINESS: Excellent for luxury goods, fashion, jewelry, entertainment, media`);
    notes.push(`RELATIONSHIPS: Intelligent partner, good communication in marriage, literary romance`);
    notes.push(`PARASHARA: "Skilled in fine arts and sciences of pleasure"`);
    notes.push(`BEST IN: 2nd (wealth through arts), 5th (creative genius), 10th (career fame)`);
    notes.push(`CHALLENGE: Can be superficial or too focused on aesthetics over substance`);
    notes.push(`ACTIVATION: Mercury dasha or Venus dasha brings artistic recognition and wealth`);
    
    return { value: 48, notes };
  }
};

// ===== JUPITER-VENUS COMBINATIONS =====

const jupiterVenusConjunction: Rule = {
  key: 'jupiter_venus_conjunction',
  areas: ['Wealth', 'Spouse', 'Children'],
  templateKey: 'jupiter_venus_conjunction',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return jupiterHouse !== undefined && jupiterHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`🪐💎 JUPITER-VENUS CONJUNCTION (Divine Grace Yoga):`);
    notes.push(`CLASSICAL: Two greatest benefics together - supreme blessing for happiness!`);
    notes.push(`EFFECTS: Wealth, luxury, beautiful spouse, good children, religious happiness`);
    notes.push(`WEALTH: Abundant prosperity, especially in Jupiter-Venus businesses (teaching, arts, luxury)`);
    notes.push(`MARRIAGE: Beautiful, cultured, wealthy spouse from good family - happy married life`);
    notes.push(`CHILDREN: Blessed with virtuous, attractive, intelligent children`);
    notes.push(`CHARACTER: Generous, kind, cultured, refined, loves beauty and wisdom equally`);
    notes.push(`PARASHARA: "Enjoys all comforts like royalty, blessed by Lakshmi and Saraswati both"`);
    notes.push(`BEST IN: Any house gives blessings, but 1st, 2nd, 4th, 5th, 7th, 9th, 11th are supreme`);
    notes.push(`CAVEAT: Can be too pleasure-loving or materialistic - remember dharma`);
    notes.push(`ACTIVATION: Jupiter dasha (16y) or Venus dasha (20y) = 36 years of peak blessings!`);
    
    return { value: 68, notes };
  }
};

// ===== MOON-MERCURY COMBINATIONS =====

const moonMercuryConjunction: Rule = {
  key: 'moon_mercury_conjunction',
  areas: ['Self', 'Career', 'Mother'],
  templateKey: 'moon_mercury_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;
    return moonHouse !== undefined && moonHouse === mercuryHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`🌙📝 MOON-MERCURY CONJUNCTION (Chandra-Budha Yoga):`);
    notes.push(`CLASSICAL: Mind meets intellect - creates skilled communicators and writers`);
    notes.push(`EFFECTS: Quick wit, excellent memory, good speaker, business acumen, adaptability`);
    notes.push(`INTELLIGENCE: Emotionally intelligent, understands people, good psychologist`);
    notes.push(`CAREER: Writing, journalism, counseling, teaching, business, trading, psychology`);
    notes.push(`COMMUNICATION: Expressive emotions through words, good storyteller, relatable speaker`);
    notes.push(`MOTHER: Mother is intelligent, communicative, possibly in education or business`);
    notes.push(`PARASHARA: "Skilled in speech and trade, clever in dealing with people"`);
    notes.push(`BEST IN: 2nd (speech, wealth), 5th (intelligence), 10th (career communication)`);
    notes.push(`CHALLENGE: Emotional mind - anxiety, overthinking, restless thoughts`);
    notes.push(`ACTIVATION: Moon dasha (10y) or Mercury dasha (17y) brings communication success`);
    
    return { value: 45, notes };
  }
};

// ===== MOON-VENUS COMBINATIONS =====

const moonVenusConjunction: Rule = {
  key: 'moon_venus_conjunction',
  areas: ['Self', 'Spouse', 'Wealth', 'Mother'],
  templateKey: 'moon_venus_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return moonHouse !== undefined && moonHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    notes.push(`🌙💎 MOON-VENUS CONJUNCTION (Emotional Beauty):`);
    notes.push(`CLASSICAL: Mind meets beauty - creates artists, lovers, aesthetically refined souls`);
    notes.push(`EFFECTS: Beautiful appearance, artistic nature, love of luxury, emotional depth`);
    notes.push(`RELATIONSHIPS: Romantic nature, attracts beautiful partners, emotional in love`);
    notes.push(`MOTHER: Beautiful, artistic mother with strong emotional bond`);
    notes.push(`CAREER: Fashion, beauty, arts, hospitality, entertainment, jewelry, luxury goods`);
    notes.push(`CHARACTER: Graceful, charming, sweet-natured, loves comfort and beauty`);
    notes.push(`PARASHARA: "Adorned with beauty and wealth, attractive to opposite sex"`);
    notes.push(`BEST IN: 1st (beautiful appearance), 2nd (wealth), 4th (luxury home), 7th (marriage)`);
    notes.push(`CHALLENGE: Emotional dependency on relationships, seeking security through love`);
    notes.push(`ACTIVATION: Moon dasha (10y) or Venus dasha (20y) brings romance and luxury`);
    
    return { value: 50, notes };
  }
};

// Export all two-planet combinations
export const TWO_PLANET_COMBINATIONS: Rule[] = [
  sunMoonConjunction,
  sunMoonOpposition,
  sunJupiterConjunction,
  sunVenusConjunction,
  moonJupiterConjunction,
  marsJupiterConjunction,
  marsVenusConjunction,
  mercuryJupiterConjunction,
  mercuryVenusConjunction,
  jupiterVenusConjunction,
  moonMercuryConjunction,
  moonVenusConjunction
];
